import { useMemo } from "react";
import { useAppSelector } from "@store/store-helper";
import {
  companyMembersByRoleSelector,
  companyMembersSelector,
  fetchingMembersFlagsSelector,
} from "@store/members/members-selector";
import { PageInfoBar } from "@components/common/page-info-bar";
import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { InviteMemberToCompany } from "@pages/members/invite-member-to-company";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";

/**
 * Shows the main details for members
 */
export function MainMemberDetails(): JSX.Element {
  const { isFetchingCompanyMembers } = useAppSelector(
    fetchingMembersFlagsSelector
  );

  const adminMembers = useAppSelector(
    companyMembersByRoleSelector("COMPANY_EXECUTIVE")
  );
  const groupManagerMembers = useAppSelector(
    companyMembersByRoleSelector("COMPANY_MANAGER")
  );
  const projectManagerMembers = useAppSelector(
    companyMembersByRoleSelector("PROJECT_MANAGER")
  );
  const viewerMembers = useAppSelector(
    companyMembersByRoleSelector("COMPANY_VIEWER")
  );
  const groupViewers = useAppSelector(
    companyMembersByRoleSelector("GROUP_VIEWER")
  );

  const companyMembers = useAppSelector(companyMembersSelector);
  const { canInviteUsersToCompany } = useHasUserValidRoleCompanyLevel();

  /**
   * Returns the total number of users in the workspace, including all roles,
   * plus members that only have project access.
   * This number is independent of any applied filters.
   */
  const totalUsersInWorkspace = useMemo(() => {
    return companyMembers.length;
  }, [companyMembers]);

  if (isFetchingCompanyMembers) {
    return <PageInfoBar isLoading={true} />;
  }

  return (
    <PageInfoBar
      items={[
        {
          content: (
            <FaroTextField
              label="Total"
              initialValue={totalUsersInWorkspace}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
        {
          content: (
            <FaroTextField
              label="Admins"
              initialValue={adminMembers.length}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
        {
          content: (
            <FaroTextField
              label="Viewers"
              initialValue={viewerMembers.length}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
        {
          content: (
            <FaroTextField
              label="Group Managers"
              initialValue={groupManagerMembers.length}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
        {
          content: (
            <FaroTextField
              label="Group Viewers"
              initialValue={groupViewers.length}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
        {
          content: (
            <FaroTextField
              label="Project Managers"
              initialValue={projectManagerMembers.length}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
      ]}
      action={{
        content: <InviteMemberToCompany />,
        isHidden: !canInviteUsersToCompany,
      }}
    />
  );
}
